import { AREA_KIND_ORDER, type AreaKind } from '../domain/areaCatalog';
import { AGREEMENT_DOCUMENT_VERSION, defaultAgreement, emptyParty, type V2Agreement, type V2AgreementParty, type V2RemoteSignature } from '../domain/agreement';
import { isInspectionService } from '../domain/inspectionService';
import { isAiConfidence, isAiObservationKind, isAiObservationResolutionKind, isAiVerdict, type AiGateLogEntry, type AiObservationResolution, type V2AiObservation, type V2AiRun } from '../domain/aiObservation';
import { deriveAiV2Verdict } from '../../supabase/functions/_shared/aiV2Protocol';
import { isAiJobLocalStatus, type V2AiJob } from '../domain/aiJob';
import { checkAiJobSubject, checkAiJobUsage, isAiJobTier, normalizeAdvancedReviewResult } from '../../supabase/functions/_shared/aiJobsProtocol';
import { isGateAction, isGateConvertedTo, isGateInspectorAction, isGateNoteCode, isGateReason, isGateSuppression, isInspectionContextKind, type GateInspectorAction, type GateNoteCode, type GateProhibitedHit, type InspectionScopeSnapshot, type ScopeDispositionRecord, type SnapshotPropertyFacts, type SuggestionScope } from '../domain/scopeGate';
import { isLegalStatus, isScopeDisposition, isScopeSubjectId } from '../domain/authority/ny/scopeSubjects';
import { isProhibitedPhraseId } from '../domain/authority/ny/prohibitedPhrases';
import { isNonClientTestMarker } from '../domain/inspectionContext';
import { isSignatureStrokes } from '../domain/signature';
import { DEFAULT_FINDING_PRIORITY, isFindingClass, isFindingPriority, type V2Finding } from '../domain/finding';
import type { AlbumSaveState, V2Evidence } from '../domain/evidence';
import { annotationsOf } from '../domain/annotation';
import { isReportFileVariant, isReportOutputLanguage, type V2ReportRecord } from '../domain/report';
import { isResponsibleTrade } from '../domain/responsibleTrade';
import { isDefectSeverity } from '../domain/severity';
import { isJurisdictionCode } from '../domain/jurisdiction';
import { isDescriptorKey, isHeatingFuel, isUtilitySource, type DescriptorAnswer } from '../domain/descriptors';
import { isLimitationTemplateId } from '../domain/authority/ny/limitationTemplates';
import { DEFAULT_REPORT_LANGUAGE, isBasementFinish, isBasementType, isInspectionContextOrigin, isItemStatus, isLicenseStatus, isPortionReason, isReportLanguage, isStartMode, isUnableReason, isUnitCount, V2_SCHEMA_VERSION, type AreaCompletionSummary, type InspectionCoverPhoto, type InspectionPurge, type InspectionStatus, type ItemStatus, type LimitedPortion, type ProgressSummary, type PropertyType, type V2Area, type V2ChecklistItem, type V2Inspection, type AreaNameLabel } from '../domain/inspection';

// Schema authority for everything read back from the yanwushi-v2 database.
//   - Every record is validated before it reaches state. A record that cannot be understood is
//     DROPPED AND COUNTED (surfaced in Settings), never silently coerced and never allowed to
//     crash boot: corrupt data must not take the inspector's other work down with it.
//   - Final Owner Decision 2026-09-14 (CA-17 / D-CA-8): the version-6 database reset emptied every
//     store, so there is exactly ONE record contract — this one. No founding-round (v1) upgrade, no
//     compatibility read of the retired suggestion / row-finding / job-adoption shapes exists any more.
//   - Fields are still OPTIONAL on disk within the contract (a record written before an additive field
//     reads back with the field's default); the schemaVersion stays 2.
export type V2StoreName = 'inspections' | 'areas' | 'items' | 'evidence' | 'findings' | 'reports' | 'aiRuns' | 'aiObservations' | 'aiJobs';

export interface RawSnapshot {
  inspections: unknown[];
  areas: unknown[];
  items: unknown[];
  evidence: unknown[];
  findings: unknown[];
  // Absent in snapshots written by tests / older code paths; read as empty.
  reports?: unknown[];
  aiRuns?: unknown[];
  aiObservations?: unknown[];
  aiJobs?: unknown[];
}

export interface NormalizedSnapshot {
  inspections: V2Inspection[];
  areas: V2Area[];
  items: V2ChecklistItem[];
  evidence: V2Evidence[];
  findings: V2Finding[];
  reports: V2ReportRecord[];
  aiRuns: V2AiRun[];
  aiObservations: V2AiObservation[];
  aiJobs: V2AiJob[];
  dropped: number;
  // Records repaired on read (an AI run interrupted mid-flight) that should be written back.
  upgraded: Array<{ store: V2StoreName; value: V2AiRun }>;
}

type Rec = Record<string, unknown>;

function isRec(v: unknown): v is Rec {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}
function str(v: unknown, fallback = ''): string {
  return typeof v === 'string' ? v : fallback;
}
function num(v: unknown, fallback: number): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}
function numOrNull(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}
function bool(v: unknown, fallback: boolean): boolean {
  return typeof v === 'boolean' ? v : fallback;
}
// A three-state fact: true / false / unknown. Anything that is not a boolean — absent, null,
// a string, a number — is UNKNOWN. It is never coerced to a yes or a no (NY F1 Round 1, PP-20:
// occupancy history is never invented by a migration).
function boolOrNull(v: unknown): boolean | null {
  return typeof v === 'boolean' ? v : null;
}
// Area.label: plan-name descriptor, or null (inspector-named areas). Never rewritten on read.
function areaLabelOrNull(v: unknown): AreaNameLabel | null {
  if (!isRec(v)) return null;
  const preset = str(v.preset);
  if (preset.length === 0) return null;
  const index = typeof v.index === 'number' && Number.isInteger(v.index) && v.index > 0 ? v.index : null;
  return { preset, index, withFloor: bool(v.withFloor, false) };
}

// Area.floor: a positive integer, or null.
function floorOrNull(v: unknown): number | null {
  return typeof v === 'number' && Number.isInteger(v) && v >= 1 ? v : null;
}
function strArray(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
}
function hasIds(r: Rec, keys: string[]): boolean {
  return keys.every((k) => typeof r[k] === 'string' && (r[k] as string).length > 0);
}

const INSPECTION_STATUSES: readonly string[] = ['draft', 'inProgress', 'completed'];
const PROPERTY_TYPES: readonly string[] = ['singleFamily', 'multiFamily', 'condo', 'townhouse', 'other'];
const ALBUM_STATES: readonly string[] = ['pending', 'saved', 'failed', 'unavailable', 'notRequired'];
const FINDING_STATUSES: readonly string[] = ['draft', 'confirmed', 'rejected'];
const FINDING_ORIGINS: readonly string[] = ['inspector', 'ai', 'system'];

// ---------- v2 validation ----------

function validateParty(v: unknown): V2AgreementParty {
  if (!isRec(v)) return emptyParty();
  return {
    name: str(v.name),
    signature: isSignatureStrokes(v.signature) ? v.signature : null,
    accepted: bool(v.accepted, false),
    signedAt: numOrNull(v.signedAt)
  };
}

// The agreement is embedded in the inspection record. Absent or malformed → the default (unsigned)
// agreement, never a drop. A record that carries signatures but no consent reads back WITHOUT consent
// (never granted by a migration) and as signed under the copy it was drawn under (OD-8 / OD-9).
export function validateAgreement(v: unknown): V2Agreement {
  const base = defaultAgreement();
  if (!isRec(v)) return base;
  const fee = isRec(v.fee) ? v.fee : {};
  const demo = bool(v.demo, false);
  const documentVersion = str(v.documentVersion, AGREEMENT_DOCUMENT_VERSION);
  const signedAt = numOrNull(v.signedAt);
  const signedVersion = typeof v.signedVersion === 'string' && v.signedVersion.length > 0 ? v.signedVersion : signedAt !== null ? documentVersion : null;
  return {
    documentVersion,
    jurisdiction: 'NY',
    // v5: a record written before the field existed reads back as English (an older copy is bilingual anyway).
    documentLanguage: v.documentLanguage === 'zh-CN' ? 'zh-CN' : 'en-US',
    fee: { amount: demo ? '' : str(fee.amount) },
    demo,
    client: validateParty(v.client),
    inspector: validateParty(v.inspector),
    clientAiConsent: bool(v.clientAiConsent, false),
    clientAiConsentAt: bool(v.clientAiConsent, false) ? numOrNull(v.clientAiConsentAt) : null,
    aiConsentWithdrawnAt: numOrNull(v.aiConsentWithdrawnAt),
    // v4: never granted by a migration — a record without the field reads back WITHOUT the consent.
    clientEsignConsent: bool(v.clientEsignConsent, false),
    clientEsignConsentAt: bool(v.clientEsignConsent, false) ? numOrNull(v.clientEsignConsentAt) : null,
    // v5: never granted by a migration either.
    clientLiabilityAck: bool(v.clientLiabilityAck, false),
    clientLiabilityAckAt: bool(v.clientLiabilityAck, false) ? numOrNull(v.clientLiabilityAckAt) : null,
    additionalServices: Array.isArray(v.additionalServices)
      ? v.additionalServices.filter(isRec).map((service) => ({ name: str(service.name), fee: str(service.fee) }))
      : [],
    signedVersion,
    signedService: isInspectionService(v.signedService) ? v.signedService : signedAt !== null ? 'homeInspection' : null,
    signedAt,
    // Remote signing (2026-10-20): written only when present, so a record without it reads back exactly as stored.
    ...remoteSignatureOf(v.clientRemote)
  };
}

function remoteSignatureOf(v: unknown): { clientRemote?: V2RemoteSignature } {
  if (!isRec(v) || typeof v.receivedAt !== 'number' || !Number.isFinite(v.receivedAt)) return {};
  return {
    clientRemote: {
      receivedAt: v.receivedAt,
      ip: typeof v.ip === 'string' && v.ip.length <= 100 ? v.ip : null,
      userAgent: typeof v.userAgent === 'string' && v.userAgent.length <= 400 ? v.userAgent : null
    }
  };
}

// NY F1 Round 2: descriptor answers read back only when each entry is a record with a catalogue key
// and a string value; the detail defaults to ''. A duplicate key keeps its first entry. Nothing is
// ever invented from an inspector note.
function descriptorAnswers(v: unknown): DescriptorAnswer[] {
  if (!Array.isArray(v)) return [];
  const out: DescriptorAnswer[] = [];
  const seen = new Set<string>();
  for (const entry of v) {
    if (!isRec(entry) || !isDescriptorKey(entry.key) || typeof entry.value !== 'string' || seen.has(entry.key)) continue;
    seen.add(entry.key);
    out.push({ key: entry.key, value: entry.value, detail: typeof entry.detail === 'string' ? entry.detail : '' });
  }
  return out;
}

// NY F1 Round 5 (OD-12): the basic-information row a purged inspection keeps. Absent → null. A record
// that says it was purged keeps its counts; missing counts read as zero rather than dropping the row.
function progressSummary(v: unknown): ProgressSummary {
  const r = isRec(v) ? v : {};
  return {
    total: num(r.total, 0),
    settled: num(r.settled, 0),
    notChecked: num(r.notChecked, 0),
    ok: num(r.ok, 0),
    issues: num(r.issues, 0),
    notApplicable: num(r.notApplicable, 0),
    unable: num(r.unable, 0)
  };
}

function areaCompletion(v: unknown): AreaCompletionSummary {
  const r = isRec(v) ? v : {};
  return { enabledAreas: num(r.enabledAreas, 0), completedAreas: num(r.completedAreas, 0) };
}

function inspectionPurge(v: unknown): InspectionPurge | null {
  if (!isRec(v)) return null;
  const purgedAt = numOrNull(v.purgedAt);
  if (purgedAt === null) return null;
  return {
    purgedAt,
    generatedAt: num(v.generatedAt, purgedAt),
    pdfCount: num(v.pdfCount, 0),
    service: isInspectionService(v.service) ? v.service : 'homeInspection',
    progress: progressSummary(v.progress),
    areas: areaCompletion(v.areas),
    findingCount: num(v.findingCount, 0)
  };
}

// ---------- NY F1 Round 4: the scope snapshot, the gate decision and the run's gate trail ----------
//
// All three are ADDITIVE and optional on disk. A record without them reads back with `null` / `[]`,
// which the Scope Gate treats as "no New York packet" and fails closed on — it never invents a
// snapshot for an inspection that was signed before the snapshot existed (frozen plan §6).
function scopeSubjectIdOrNull(v: unknown): string | null {
  return isScopeSubjectId(v) ? v : null;
}

function dispositionRecords(v: unknown): Record<string, ScopeDispositionRecord> {
  if (!isRec(v)) return {};
  const out: Record<string, ScopeDispositionRecord> = {};
  for (const [key, value] of Object.entries(v)) {
    if (!isScopeSubjectId(key) || !isRec(value) || !isLegalStatus(value.legalStatus) || !isScopeDisposition(value.disposition)) continue;
    out[key] = { legalStatus: value.legalStatus, disposition: value.disposition, ruleIds: strArray(value.ruleIds) };
  }
  return out;
}

function snapshotFacts(v: unknown): SnapshotPropertyFacts {
  const r = isRec(v) ? v : {};
  const propertyType = str(r.propertyType);
  return {
    propertyType: PROPERTY_TYPES.includes(propertyType) ? (propertyType as PropertyType) : null,
    yearBuilt: numOrNull(r.yearBuilt),
    basementType: isBasementType(r.basementType) ? r.basementType : null,
    basementFinish: isBasementFinish(r.basementFinish) ? r.basementFinish : null,
    previouslyOccupied: boolOrNull(r.previouslyOccupied),
    waterSupplySource: isUtilitySource(r.waterSupplySource) ? r.waterSupplySource : null,
    wasteDisposalSource: isUtilitySource(r.wasteDisposalSource) ? r.wasteDisposalSource : null,
    heatingFuel: isHeatingFuel(r.heatingFuel) ? r.heatingFuel : null
  };
}

export function validateScopeSnapshot(v: unknown): InspectionScopeSnapshot | null {
  if (!isRec(v)) return null;
  // A snapshot is only a snapshot when it names the authority that produced it. Anything else — an
  // empty object, a half-written record, another jurisdiction — reads back as no snapshot.
  if (v.jurisdiction !== 'NY') return null;
  const authorityVersion = str(v.authorityVersion);
  if (authorityVersion.length === 0) return null;
  const dispositions = dispositionRecords(v.dispositions);
  if (Object.keys(dispositions).length === 0) return null;
  const block = isRec(v.legalReviewBlock) && str(v.legalReviewBlock.blockerId).length > 0 ? { blockerId: str(v.legalReviewBlock.blockerId), lrIds: strArray(v.legalReviewBlock.lrIds) } : null;
  return {
    jurisdiction: 'NY',
    authorityVersion,
    agreementAuthorityVersion: str(v.agreementAuthorityVersion),
    checklistAuthorityVersion: str(v.checklistAuthorityVersion),
    takenAt: num(v.takenAt, 0),
    propertyFacts: snapshotFacts(v.propertyFacts),
    dispositions,
    contractAdded: strArray(v.contractAdded).filter(isScopeSubjectId),
    clientExclusions: strArray(v.clientExclusions).filter(isScopeSubjectId),
    // Absent or forged → the fail-closed side: a real client engagement (PP-21).
    contextKind: isInspectionContextKind(v.contextKind) ? v.contextKind : 'realClient',
    legalReviewBlock: block
  };
}

function prohibitedHits(v: unknown): GateProhibitedHit[] {
  if (!Array.isArray(v)) return [];
  const out: GateProhibitedHit[] = [];
  for (const entry of v) {
    if (!isRec(entry) || !isProhibitedPhraseId(entry.phraseId)) continue;
    const field = str(entry.field);
    if (field !== 'title' && field !== 'headline' && field !== 'observation' && field !== 'recommendation') continue;
    out.push({ phraseId: entry.phraseId, field, sentence: str(entry.sentence) });
  }
  return out;
}

export function validateSuggestionScope(v: unknown): SuggestionScope | null {
  if (!isRec(v) || !isGateAction(v.action) || !isGateReason(v.reason)) return null;
  const limitation = isRec(v.limitation) && isUnableReason(v.limitation.reasonCode)
    ? {
        targetItemId: typeof v.limitation.targetItemId === 'string' ? v.limitation.targetItemId : null,
        targetRow: typeof v.limitation.targetRow === 'string' ? v.limitation.targetRow : null,
        reasonCode: v.limitation.reasonCode,
        templateId: isLimitationTemplateId(v.limitation.templateId) ? v.limitation.templateId : null,
        scope: 'partial' as const
      }
    : null;
  const prompt = str(v.descriptorPrompt);
  return {
    authorityVersion: str(v.authorityVersion),
    subjectId: scopeSubjectIdOrNull(v.subjectId) as SuggestionScope['subjectId'],
    legalStatus: isLegalStatus(v.legalStatus) ? v.legalStatus : null,
    disposition: isScopeDisposition(v.disposition) ? v.disposition : null,
    action: v.action,
    convertedTo: isGateConvertedTo(v.convertedTo) ? v.convertedTo : null,
    reason: v.reason,
    ruleIds: strArray(v.ruleIds),
    noteCodes: strArray(v.noteCodes).filter(isGateNoteCode) as GateNoteCode[],
    prohibitedHits: prohibitedHits(v.prohibitedHits),
    limitation,
    descriptorPrompt: prompt === 'waterSupplySource' || prompt === 'wasteDisposalSource' ? prompt : null,
    allowedActions: strArray(v.allowedActions).filter(isGateInspectorAction) as GateInspectorAction[],
    relatedItemId: typeof v.relatedItemId === 'string' ? v.relatedItemId : null
  };
}

function gateLog(v: unknown): AiGateLogEntry[] {
  if (!Array.isArray(v)) return [];
  const out: AiGateLogEntry[] = [];
  for (const entry of v) {
    if (!isRec(entry) || !isGateAction(entry.action) || !isGateReason(entry.reason)) continue;
    const original = isRec(entry.original) ? entry.original : {};
    if (!isAiObservationKind(original.type)) continue;
    out.push({
      resultId: str(entry.resultId),
      observationId: typeof entry.observationId === 'string' ? entry.observationId : null,
      action: entry.action,
      reason: entry.reason,
      subjectId: scopeSubjectIdOrNull(entry.subjectId),
      disposition: isScopeDisposition(entry.disposition) ? entry.disposition : null,
      legalStatus: isLegalStatus(entry.legalStatus) ? entry.legalStatus : null,
      suppressed: isGateSuppression(entry.suppressed) ? entry.suppressed : null,
      ruleIds: strArray(entry.ruleIds),
      authorityVersion: str(entry.authorityVersion),
      prohibitedHits: prohibitedHits(entry.prohibitedHits),
      original: {
        type: original.type,
        itemId: typeof original.itemId === 'string' ? original.itemId : null,
        title: str(original.title),
        headline: str(original.headline),
        observation: str(original.observation),
        recommendation: str(original.recommendation)
      }
    });
  }
  return out;
}

// Owner 2026-09-14: the report cover photo facts. Absent or malformed → null (no cover); the bytes are
// looked up by the deterministic media key, never stored on the record.
function validateCoverPhoto(v: unknown): InspectionCoverPhoto | null {
  if (!isRec(v)) return null;
  const source = str(v.source);
  if (source !== 'camera' && source !== 'photoLibrary') return null;
  const albumSaveState = str(v.albumSaveState);
  if (!ALBUM_STATES.includes(albumSaveState)) return null;
  return {
    source,
    mimeType: str(v.mimeType, 'image/jpeg'),
    byteSize: num(v.byteSize, 0),
    width: numOrNull(v.width),
    height: numOrNull(v.height),
    albumSaveState: albumSaveState as AlbumSaveState,
    albumSaveDetail: str(v.albumSaveDetail),
    version: num(v.version, 1),
    updatedAt: num(v.updatedAt, 0)
  };
}

export function validateInspection(v: unknown): V2Inspection | null {
  if (!isRec(v) || v.schemaVersion !== V2_SCHEMA_VERSION || !hasIds(v, ['id'])) return null;
  const property = isRec(v.property) ? v.property : null;
  const client = isRec(v.client) ? v.client : {};
  const inspector = isRec(v.inspector) ? v.inspector : {};
  if (!property) return null;
  const status = str(v.status);
  if (!INSPECTION_STATUSES.includes(status)) return null;
  const propertyType = str(property.propertyType);
  const createdAt = num(v.createdAt, 0);
  return {
    schemaVersion: V2_SCHEMA_VERSION,
    id: v.id as string,
    property: {
      address: str(property.address),
      city: str(property.city),
      state: str(property.state),
      zip: str(property.zip),
      // NY F1 Round 1: a value that came from a form default is not an inspector assertion (RT-04).
      stateConfirmed: bool(property.stateConfirmed, false),
      // Absent → null (UNKNOWN). Occupancy history is NEVER inferred or invented on read (PP-20).
      previouslyOccupied: boolOrNull(property.previouslyOccupied),
      propertyType: PROPERTY_TYPES.includes(propertyType) ? (propertyType as PropertyType) : null,
      // NY F1 Round 5 (CK-10): absent → null (never asked); never inferred from the property type.
      unitCount: isUnitCount(property.unitCount) ? property.unitCount : null,
      yearBuilt: numOrNull(property.yearBuilt),
      basementType: isBasementType(property.basementType) ? property.basementType : null,
      basementFinish: isBasementFinish(property.basementFinish) ? property.basementFinish : null,
      // NY F1 Round 2: absent → null (never asked). Only a listed value is an answer.
      waterSupplySource: isUtilitySource(property.waterSupplySource) ? property.waterSupplySource : null,
      wasteDisposalSource: isUtilitySource(property.wasteDisposalSource) ? property.wasteDisposalSource : null,
      heatingFuel: isHeatingFuel(property.heatingFuel) ? property.heatingFuel : null
    },
    client: { name: str(client.name), phone: str(client.phone), email: str(client.email) },
    inspector: {
      name: str(inspector.name),
      // Absent → null / '' (RP-01). A licence assertion is never fabricated on read.
      licensedState: isJurisdictionCode(inspector.licensedState) ? inspector.licensedState : null,
      licenseNumber: str(inspector.licenseNumber),
      licenseStatus: isLicenseStatus(inspector.licenseStatus) ? inspector.licenseStatus : null
    },
    status: status as InspectionStatus,
    weather: str(v.weather),
    notes: str(v.notes),
    reportLanguage: isReportLanguage(v.reportLanguage) ? v.reportLanguage : DEFAULT_REPORT_LANGUAGE,
    clientChineseSummary: v.clientChineseSummary === true,
    inspectionAt: num(v.inspectionAt, createdAt),
    startMode: isStartMode(v.startMode) ? v.startMode : null,
    agreement: validateAgreement(v.agreement),
    // Absent → null, which contextKindOf() reads as a real client engagement: the fail-closed side of PP-21.
    contextOrigin: isInspectionContextOrigin(v.contextOrigin) ? v.contextOrigin : null,
    // NY F1 Round 4: absent, malformed or from another authority version → null. Never reconstructed.
    scopeSnapshot: validateScopeSnapshot(v.scopeSnapshot),
    finalizedAt: numOrNull(v.finalizedAt),
    purge: inspectionPurge(v.purge),
    coverPhoto: validateCoverPhoto(v.coverPhoto),
    // Owner 2026-10-20: absent → null (worked on here). A copy is never marked as moved on read.
    movedOut: isRec(v.movedOut) && typeof v.movedOut.at === 'number' && Number.isFinite(v.movedOut.at) ? { at: v.movedOut.at, fileName: str(v.movedOut.fileName).slice(0, 200) } : null,
    createdAt,
    updatedAt: num(v.updatedAt, 0),
    completedAt: numOrNull(v.completedAt)
  };
}

// Written only when the area has answers, so an area without them reads back exactly as stored.
function shotSkipsOf(v: unknown): { shotSkips?: string[] } {
  const keys = [...new Set(strArray(v).filter((key) => key.length > 0 && key.length <= 40))].slice(0, 40);
  return keys.length > 0 ? { shotSkips: keys } : {};
}

export function validateArea(v: unknown): V2Area | null {
  if (!isRec(v) || v.schemaVersion !== V2_SCHEMA_VERSION || !hasIds(v, ['id', 'inspectionId'])) return null;
  const kind = str(v.kind);
  if (!(AREA_KIND_ORDER as readonly string[]).includes(kind)) return null;
  return {
    schemaVersion: V2_SCHEMA_VERSION,
    id: v.id as string,
    inspectionId: v.inspectionId as string,
    kind: kind as AreaKind,
    name: str(v.name, kind),
    enabled: bool(v.enabled, true),
    floor: floorOrNull(v.floor),
    label: areaLabelOrNull(v.label),
    aiSkipped: bool(v.aiSkipped, false),
    scanNote: str(v.scanNote),
    note: str(v.note),
    // 2026-10-19 (Owner item 31): absent → []; at most 40 keys of at most 40 characters.
    ...shotSkipsOf(v.shotSkips),
    completedAt: numOrNull(v.completedAt),
    sortOrder: num(v.sortOrder, 0),
    createdAt: num(v.createdAt, 0),
    updatedAt: num(v.updatedAt, 0)
  };
}

// NY F1 Round 3: absent → []. A portion is kept only when well-formed (a portion reason, string extent /
// note, a known template id or null); a row whose status cannot carry portions reads back with none.
function limitedPortions(v: unknown, status: ItemStatus): LimitedPortion[] {
  if (!Array.isArray(v) || status === 'unableToInspect' || status === 'notApplicable') return [];
  const out: LimitedPortion[] = [];
  for (const entry of v) {
    if (!isRec(entry) || !isPortionReason(entry.reasonCode)) continue;
    out.push({ reasonCode: entry.reasonCode, extent: str(entry.extent), note: str(entry.note), templateId: isLimitationTemplateId(entry.templateId) ? entry.templateId : null });
  }
  return out;
}

function itemReportSentence(v: unknown): V2ChecklistItem['reportSentence'] {
  if (!isRec(v) || typeof v.en !== 'string' || typeof v.zh !== 'string' || typeof v.basis !== 'string') return null;
  const source = v.source === 'inspector' ? 'inspector' : 'ai';
  // 2026-09-21 (additive, optional on disk): the language the inspector wrote last. Absent → English,
  // the only side that could be edited before the field existed; never carried by an AI sentence.
  return { en: v.en, zh: v.zh, source, basis: v.basis, at: num(v.at, 0), ...(source === 'inspector' ? { edited: v.edited === 'zh' ? ('zh' as const) : ('en' as const) } : {}) };
}

export function validateItem(v: unknown): V2ChecklistItem | null {
  if (!isRec(v) || v.schemaVersion !== V2_SCHEMA_VERSION || !hasIds(v, ['id', 'inspectionId', 'areaId'])) return null;
  if (!isItemStatus(v.status)) return null;
  const storedEvidenceIds = strArray(v.evidenceIds);
  // An override and a link never coexist for one photo: the inspector's latest act decided, and the
  // link list is what the row shows — an exclusion that still names a linked photo is dropped.
  const evidenceExclusions = strArray(v.evidenceExclusions).filter((id) => !storedEvidenceIds.includes(id));
  // 2026-10-27: an AI placement is a link marked 「AI 放的」. A row written 2026-10-18 … 10-26 still holds
  // proposals that were not links — they become AI-placed links on read (the mark stays, so the
  // inspector sees which ones the AI put there). A mark on an excluded photo is settled and reads back gone.
  const evidenceSuggestions = strArray(v.evidenceSuggestions).filter((id) => !evidenceExclusions.includes(id));
  const evidenceIds = [...new Set([...storedEvidenceIds, ...evidenceSuggestions])];
  return {
    schemaVersion: V2_SCHEMA_VERSION,
    id: v.id as string,
    inspectionId: v.inspectionId as string,
    areaId: v.areaId as string,
    templateId: typeof v.templateId === 'string' ? v.templateId : null,
    title: str(v.title),
    hint: str(v.hint),
    status: v.status,
    inspectorNote: str(v.inspectorNote),
    statusReason: str(v.statusReason),
    reasonCode: isUnableReason(v.reasonCode) ? v.reasonCode : null,
    descriptors: descriptorAnswers(v.descriptors),
    limitedPortions: limitedPortions(v.limitedPortions, v.status),
    evidenceIds,
    // Final Owner Decision 2026-09-14 (CA-7): absent → [] (no override recorded).
    evidenceExclusions: [...new Set(evidenceExclusions)],
    // 2026-10-18: absent → [] (nothing proposed).
    evidenceSuggestions: [...new Set(evidenceSuggestions)],
    reportSentence: itemReportSentence(v.reportSentence),
    operationalTestPerformed: bool(v.operationalTestPerformed, false),
    // 2026-09-13 ratification §4, on the row since 2026-09-14: absent → false (never inferred).
    structuralConcern: bool(v.structuralConcern, false),
    // 2026-10-19: the inspector's defect tier; absent or unknown → null (the suggested tier prints).
    severity: isDefectSeverity(v.severity) ? v.severity : null,
    sortOrder: num(v.sortOrder, 0),
    createdAt: num(v.createdAt, 0),
    updatedAt: num(v.updatedAt, 0)
  };
}

// Evidence: `areaId` is the capture location (an area) or null for photos imported into the
// inspection's photo pool (Owner task book §9) — they belong to the inspection, not to an area.
export function validateEvidence(v: unknown): V2Evidence | null {
  if (!isRec(v) || v.schemaVersion !== V2_SCHEMA_VERSION || !hasIds(v, ['id', 'inspectionId'])) return null;
  const source = str(v.source);
  if (source !== 'camera' && source !== 'photoLibrary') return null;
  const albumSaveState = str(v.albumSaveState);
  if (!ALBUM_STATES.includes(albumSaveState)) return null;
  return {
    schemaVersion: V2_SCHEMA_VERSION,
    id: v.id as string,
    inspectionId: v.inspectionId as string,
    areaId: typeof v.areaId === 'string' && v.areaId.length > 0 ? v.areaId : null,
    itemId: typeof v.itemId === 'string' ? v.itemId : null,
    source,
    localUri: str(v.localUri),
    mimeType: str(v.mimeType, 'image/jpeg'),
    byteSize: num(v.byteSize, 0),
    width: numOrNull(v.width),
    height: numOrNull(v.height),
    albumSaveState: albumSaveState as AlbumSaveState,
    albumSaveDetail: str(v.albumSaveDetail),
    caption: str(v.caption),
    // 2026-10-19: the photo-checklist shot (absent → null) and the marks drawn on the photo (absent → []).
    // Written only when present, so a record without them reads back exactly as stored.
    ...(typeof v.shot === 'string' && v.shot.length > 0 && v.shot.length <= 60 ? { shot: v.shot } : {}),
    ...(annotationsOf(v.annotations).length > 0 ? { annotations: annotationsOf(v.annotations) } : {}),
    createdAt: num(v.createdAt, 0),
    updatedAt: num(v.updatedAt, 0)
  };
}

// An OTHER FINDING of an area (CA-15 / D-CA-3): a record that still names a checklist row is from the
// retired row-finding contract and is not part of this database any more — dropped and counted.
export function validateFinding(v: unknown): V2Finding | null {
  if (!isRec(v) || v.schemaVersion !== V2_SCHEMA_VERSION || !hasIds(v, ['id', 'inspectionId', 'areaId'])) return null;
  if (typeof v.itemId === 'string' && v.itemId.length > 0) return null;
  const status = str(v.status);
  const origin = str(v.origin);
  if (!FINDING_STATUSES.includes(status) || !FINDING_ORIGINS.includes(origin)) return null;
  if (!isFindingClass(v.findingClass)) return null;
  return {
    schemaVersion: V2_SCHEMA_VERSION,
    id: v.id as string,
    inspectionId: v.inspectionId as string,
    areaId: v.areaId as string,
    origin: origin as V2Finding['origin'],
    title: str(v.title),
    observation: str(v.observation),
    recommendation: str(v.recommendation),
    responsibleTrade: isResponsibleTrade(v.responsibleTrade) ? v.responsibleTrade : null,
    responsibleTradeOther: str(v.responsibleTradeOther),
    reportSection: str(v.reportSection),
    priority: isFindingPriority(v.priority) ? v.priority : DEFAULT_FINDING_PRIORITY,
    findingClass: v.findingClass,
    status: status as V2Finding['status'],
    voluntaryObservation: bool(v.voluntaryObservation, false),
    structuralConcern: bool(v.structuralConcern, false),
    evidenceIds: strArray(v.evidenceIds),
    createdAt: num(v.createdAt, 0),
    updatedAt: num(v.updatedAt, 0)
  };
}

export function validateReport(v: unknown): V2ReportRecord | null {
  if (!isRec(v) || v.schemaVersion !== V2_SCHEMA_VERSION || !hasIds(v, ['id', 'inspectionId', 'fileName'])) return null;
  if (!isReportOutputLanguage(v.language) || !isReportFileVariant(v.variant)) return null;
  return {
    schemaVersion: V2_SCHEMA_VERSION,
    id: v.id as string,
    inspectionId: v.inspectionId as string,
    language: v.language,
    variant: v.variant,
    fileName: v.fileName as string,
    mimeType: 'application/pdf',
    byteSize: num(v.byteSize, 0),
    pageCount: num(v.pageCount, 0),
    generatedAt: num(v.generatedAt, 0)
  };
}

// AI area analysis run. A run that was still 'running' when the app died reads back as failed
// ('interrupted') and is reported for write-back: the in-flight request cannot be resumed.
export function validateAiRun(v: unknown): { run: V2AiRun; repaired: boolean } | null {
  if (!isRec(v) || v.schemaVersion !== V2_SCHEMA_VERSION || !hasIds(v, ['id', 'inspectionId', 'areaId'])) return null;
  const status = str(v.status);
  if (status !== 'running' && status !== 'done' && status !== 'failed') return null;
  const createdAt = num(v.createdAt, 0);
  const repaired = status === 'running';
  return {
    repaired,
    run: {
      schemaVersion: V2_SCHEMA_VERSION,
      id: v.id as string,
      inspectionId: v.inspectionId as string,
      areaId: v.areaId as string,
      photoIds: strArray(v.photoIds),
      scanNote: str(v.scanNote),
      status: repaired ? 'failed' : status,
      errorCode: repaired ? 'interrupted' : typeof v.errorCode === 'string' ? v.errorCode : null,
      model: typeof v.model === 'string' ? v.model : null,
      inputTokens: numOrNull(v.inputTokens),
      outputTokens: numOrNull(v.outputTokens),
      durationMs: numOrNull(v.durationMs),
      requestId: typeof v.requestId === 'string' ? v.requestId : null,
      summary: str(v.summary),
      // Only the exact marker reads back as a marker; anything else falls to null, the client-run side.
      contextMarker: isNonClientTestMarker(v.contextMarker) ? v.contextMarker : null,
      gateLog: gateLog(v.gateLog),
      startedAt: num(v.startedAt, createdAt),
      finishedAt: repaired ? num(v.updatedAt, createdAt) : numOrNull(v.finishedAt),
      createdAt,
      updatedAt: num(v.updatedAt, createdAt)
    }
  };
}

function observationResolution(v: unknown): AiObservationResolution | null {
  if (!isRec(v) || !isAiObservationResolutionKind(v.kind)) return null;
  const at = num(v.at, 0);
  switch (v.kind) {
    case 'filedToItem':
      return typeof v.itemId === 'string' && v.itemId.length > 0 ? { kind: 'filedToItem', itemId: v.itemId, at } : null;
    case 'limitation':
      return typeof v.itemId === 'string' && v.itemId.length > 0 ? { kind: 'limitation', itemId: v.itemId, at } : null;
    case 'otherFinding':
      return typeof v.findingId === 'string' && v.findingId.length > 0 ? { kind: 'otherFinding', findingId: v.findingId, at } : null;
    case 'noted':
      return { kind: 'noted', at };
  }
}

export function validateAiObservation(v: unknown): V2AiObservation | null {
  if (!isRec(v) || v.schemaVersion !== V2_SCHEMA_VERSION || !hasIds(v, ['id', 'inspectionId', 'areaId', 'runId'])) return null;
  if (!isAiObservationKind(v.type)) return null;
  return {
    schemaVersion: V2_SCHEMA_VERSION,
    id: v.id as string,
    inspectionId: v.inspectionId as string,
    areaId: v.areaId as string,
    runId: v.runId as string,
    type: v.type,
    itemId: typeof v.itemId === 'string' && v.itemId.length > 0 ? v.itemId : null,
    title: str(v.title),
    headline: str(v.headline),
    observation: str(v.observation),
    concern: str(v.concern),
    guidance: str(v.guidance),
    checkSteps: strArray(v.checkSteps),
    recommendation: str(v.recommendation),
    responsibleTrade: isResponsibleTrade(v.responsibleTrade) ? v.responsibleTrade : null,
    observationUi: typeof v.observationUi === 'string' && v.observationUi.length > 0 ? v.observationUi : null,
    recommendationUi: typeof v.recommendationUi === 'string' && v.recommendationUi.length > 0 ? v.recommendationUi : null,
    confidence: isAiConfidence(v.confidence) ? v.confidence : 'low',
    // 2026-10-18: an observation written before the field reads back as 'defect' only when it is one —
    // the type and the sentences decide, the same way the protocol reads an older server's answer.
    verdict: isAiVerdict(v.verdict)
      ? v.verdict
      : deriveAiV2Verdict({ type: v.type, observation: str(v.observation), observationUi: typeof v.observationUi === 'string' ? v.observationUi : null, headline: str(v.headline), guidance: str(v.guidance) }),
    question: typeof v.question === 'string' ? v.question : null,
    scopeNote: typeof v.scopeNote === 'string' ? v.scopeNote : null,
    photoIds: strArray(v.photoIds),
    scope: validateSuggestionScope(v.scope),
    resolution: observationResolution(v.resolution),
    sortOrder: num(v.sortOrder, 0),
    createdAt: num(v.createdAt, 0),
    updatedAt: num(v.updatedAt, 0)
  };
}

// Advanced AI job (about ONE checklist row). A job the device was still uploading or submitting when
// the app died reads back as 'submitting': the next sync asks the server whether it exists and settles
// it — never a silent "failed", never a silent second submission (Owner contract 2026-09-13 §20).
export function validateAiJob(v: unknown): V2AiJob | null {
  if (!isRec(v) || v.schemaVersion !== V2_SCHEMA_VERSION || !hasIds(v, ['id', 'inspectionId', 'areaId', 'itemId'])) return null;
  if (!isAiJobTier(v.tier) || !isAiJobLocalStatus(v.status)) return null;
  const createdAt = num(v.createdAt, 0);
  const status = v.status === 'pendingUpload' ? 'submitting' : v.status;
  return {
    schemaVersion: V2_SCHEMA_VERSION,
    id: v.id as string,
    tier: v.tier,
    inspectionId: v.inspectionId as string,
    areaId: v.areaId as string,
    itemId: v.itemId as string,
    photoIds: strArray(v.photoIds),
    inspectorNote: str(v.inspectorNote),
    subject: checkAiJobSubject(v.subject),
    followUpOf: typeof v.followUpOf === 'string' && v.followUpOf.length > 0 ? v.followUpOf : null,
    status,
    attempt: Math.max(0, Math.floor(num(v.attempt, 0))),
    errorCode: typeof v.errorCode === 'string' ? v.errorCode : null,
    retryable: bool(v.retryable, false),
    result: v.result === null || v.result === undefined ? null : normalizeAdvancedReviewResult(v.result),
    usage: v.usage === null || v.usage === undefined ? null : checkAiJobUsage(v.usage),
    contextMarker: isNonClientTestMarker(v.contextMarker) ? v.contextMarker : null,
    createdAt,
    updatedAt: num(v.updatedAt, createdAt),
    submittedAt: numOrNull(v.submittedAt),
    completedAt: numOrNull(v.completedAt),
    viewedAt: numOrNull(v.viewedAt),
    lastSyncAt: numOrNull(v.lastSyncAt)
  };
}

// ---------- snapshot normalization ----------

export function normalizeSnapshot(raw: RawSnapshot): NormalizedSnapshot {
  const out: NormalizedSnapshot = {
    inspections: [],
    areas: [],
    items: [],
    evidence: [],
    findings: [],
    reports: [],
    aiRuns: [],
    aiObservations: [],
    aiJobs: [],
    dropped: 0,
    upgraded: []
  };

  for (const r of raw.inspections) {
    const v2 = validateInspection(r);
    if (v2) out.inspections.push(v2);
    else out.dropped += 1;
  }

  for (const r of raw.areas) {
    const v2 = validateArea(r);
    if (v2) out.areas.push(v2);
    else out.dropped += 1;
  }

  for (const r of raw.items) {
    const v2 = validateItem(r);
    if (v2) out.items.push(v2);
    else out.dropped += 1;
  }

  for (const r of raw.evidence) {
    const v2 = validateEvidence(r);
    if (v2) out.evidence.push(v2);
    else out.dropped += 1;
  }

  for (const r of raw.findings) {
    const v2 = validateFinding(r);
    if (v2) out.findings.push(v2);
    else out.dropped += 1;
  }

  for (const r of raw.reports ?? []) {
    const v2 = validateReport(r);
    if (v2) out.reports.push(v2);
    else out.dropped += 1;
  }

  for (const r of raw.aiRuns ?? []) {
    const v2 = validateAiRun(r);
    if (!v2) {
      out.dropped += 1;
      continue;
    }
    out.aiRuns.push(v2.run);
    if (v2.repaired) out.upgraded.push({ store: 'aiRuns', value: v2.run });
  }

  for (const r of raw.aiObservations ?? []) {
    const v2 = validateAiObservation(r);
    if (v2) out.aiObservations.push(v2);
    else out.dropped += 1;
  }

  for (const r of raw.aiJobs ?? []) {
    const v2 = validateAiJob(r);
    if (v2) out.aiJobs.push(v2);
    else out.dropped += 1;
  }

  // Referential integrity: children of a missing parent are dropped rather than left dangling.
  const inspectionIds = new Set(out.inspections.map((i) => i.id));
  const keepArea = (a: V2Area) => inspectionIds.has(a.inspectionId);
  out.dropped += out.areas.filter((a) => !keepArea(a)).length;
  out.areas = out.areas.filter(keepArea);
  const areaIds = new Set(out.areas.map((a) => a.id));
  const keepChild = (c: { inspectionId: string; areaId: string }) => inspectionIds.has(c.inspectionId) && areaIds.has(c.areaId);
  out.dropped += out.items.filter((i) => !keepChild(i)).length;
  out.items = out.items.filter(keepChild);
  // Pool photos (areaId null) only need their inspection; area photos need their area too.
  const keepEvidence = (e: V2Evidence) => inspectionIds.has(e.inspectionId) && (e.areaId === null || areaIds.has(e.areaId));
  out.dropped += out.evidence.filter((e) => !keepEvidence(e)).length;
  out.evidence = out.evidence.filter(keepEvidence);
  out.dropped += out.findings.filter((f) => !keepChild(f)).length;
  out.findings = out.findings.filter(keepChild);
  const keepReport = (r: V2ReportRecord) => inspectionIds.has(r.inspectionId);
  out.dropped += out.reports.filter((r) => !keepReport(r)).length;
  out.reports = out.reports.filter(keepReport);
  out.dropped += out.aiRuns.filter((r) => !keepChild(r)).length;
  out.aiRuns = out.aiRuns.filter(keepChild);
  out.dropped += out.aiObservations.filter((s) => !keepChild(s)).length;
  out.aiObservations = out.aiObservations.filter(keepChild);
  // A job is about one checklist row: a job whose row is gone is gone with it.
  const itemIds = new Set(out.items.map((i) => i.id));
  const keepJob = (j: V2AiJob) => keepChild(j) && itemIds.has(j.itemId);
  out.dropped += out.aiJobs.filter((j) => !keepJob(j)).length;
  out.aiJobs = out.aiJobs.filter(keepJob);

  // Deterministic ordering regardless of IndexedDB key order.
  out.inspections.sort((a, b) => b.updatedAt - a.updatedAt || a.id.localeCompare(b.id));
  out.areas.sort((a, b) => a.sortOrder - b.sortOrder || a.createdAt - b.createdAt || a.id.localeCompare(b.id));
  out.items.sort((a, b) => a.sortOrder - b.sortOrder || a.createdAt - b.createdAt || a.id.localeCompare(b.id));
  out.evidence.sort((a, b) => a.createdAt - b.createdAt || a.id.localeCompare(b.id));
  out.findings.sort((a, b) => a.createdAt - b.createdAt || a.id.localeCompare(b.id));
  out.reports.sort((a, b) => a.generatedAt - b.generatedAt || a.id.localeCompare(b.id));
  out.aiRuns.sort((a, b) => a.startedAt - b.startedAt || a.id.localeCompare(b.id));
  out.aiObservations.sort((a, b) => a.createdAt - b.createdAt || a.sortOrder - b.sortOrder || a.id.localeCompare(b.id));
  out.aiJobs.sort((a, b) => a.createdAt - b.createdAt || a.id.localeCompare(b.id));
  return out;
}
